import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Mic, Sparkles, FileText, Link2, FileUp, ImageIcon, 
  ShieldCheck, Activity, Layers, AlertCircle, X, Loader2, Clock,
  ChevronDown, Zap, Globe, History, Trash2
} from "lucide-react";
import Header from "../components/nayan/Header";
import ResultsDashboard from "../components/nayan/ResultsDashboard";
import AnalysisAnimation from "../components/nayan/AnalysisAnimation";
import Footer from "../components/nayan/Footer";
import {
  fetchModels, analyzeText, analyzeUpload, fetchHistory, fetchAnalysis, deleteAnalysis,
} from "../lib/api";
import { formatRelative } from "../lib/format";
import JarvisVoicePanel from "../components/nayan/JarvisVoicePanel";
import useJarvisVoice from "../lib/useJarvisVoice";

const LOADING_STAGES = [
  "Reading the content carefully…",
  "Cross-checking sources…",
  "Weighing tone and framing…",
  "Looking for internal contradictions…",
  "Drafting the verdict…",
];

const FEATURE_PILLS = [
  { icon: ShieldCheck, label: "Truthfulness" },
  { icon: Activity, label: "Tone & Bias" },
  { icon: Layers, label: "Cross-source" },
  { icon: Mic, label: "Voice mode" },
];

const INPUT_TABS = [
  { id: "text", label: "Text", icon: FileText, hint: "Paste article, claim, or paragraph" },
  { id: "url", label: "Link", icon: Link2, hint: "Drop in any web URL" },
  { id: "pdf", label: "PDF", icon: FileUp, hint: "Upload a document" },
  { id: "image", label: "Image", icon: ImageIcon, hint: "Check an image (text extraction)" },
];

const EXAMPLES = [
  {
    label: "English: Viral health claim",
    text: "Drinking lemon water on an empty stomach every morning detoxifies your liver, completely cures cancer, and helps you lose 20 pounds in a month according to a recent Harvard study published last week.",
  },
  {
    label: "English: Balanced news excerpt",
    text: "The Federal Reserve held interest rates steady at its July meeting, with Chair Jerome Powell citing easing inflation but persistent labour market strength. Most economists surveyed by Reuters expect a rate cut by September.",
  },
  {
    label: "Hindi: WhatsApp forward hoax",
    text: "ब्रेकिंग न्यूज़: कल रात की रैली में नेता जी ने कहा कि अगर वे चुनाव जीतते हैं तो सभी के बैंक खातों से 50% पैसा काट लिया जाएगा। यह वीडियो सोशल मीडिया पर वायरल हो रहा है।",
  },
  {
    label: "Hindi: Scientific fact",
    text: "चंद्रयान-3 ने चंद्रमा के दक्षिणी ध्रुव पर सफलतापूर्वक लैंडिंग की है, जिससे भारत ऐसा करने वाला दुनिया का पहला देश बन गया है। इसरो ने लैंडिंग के बाद की कुछ तस्वीरें भी साझा की हैं।",
  },
  {
    label: "Hinglish: Fake circular",
    text: "Guys please forward this! UNESCO ne declare kiya hai ki Indian national anthem is the best in the world. Kal raat 8 baje sabko apne balcony me aake gaana hai varna fine lagega.",
  },
  {
    label: "Hinglish: Opinion & exaggeration",
    text: "Bhai yeh nayi movie ekdum masterpiece hai. The VFX is mind blowing and story itni emotional hai ki sab rone lage. Best movie of the decade for sure, 10/10 rating everywhere.",
  },
];

export default function WorkspacePage() {
  const navigate = useNavigate();
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("gemini-2.5-flash");
  const [loading, setLoading] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [prefilledText, setPrefilledText] = useState("");
  
  // Input states
  const [activeTab, setActiveTab] = useState("text");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [showExamples, setShowExamples] = useState(false);
  const [showModelSelect, setShowModelSelect] = useState(false);
  
  const resultsSectionRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputSectionRef = useRef(null);

  // Jarvis voice logic
  const [showVoice, setShowVoice] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const {
    supported, phase, language, setLanguage, userCaption, aiCaption,
    isInterim, audioLevel, analyser, startListening, stopAll, introduce,
    voices, isIntroduced, selectedVoiceName, preferredVoiceName, setPreferredVoiceName
  } = useJarvisVoice({
    autoStart: false,
    onError: (msg) => { setVoiceError(msg); setTimeout(() => setVoiceError(""), 5000); }
  });

  const handleVoiceTrigger = () => {
    setShowVoice(true);
    if (!isIntroduced && phase !== "introducing") {
      introduce();
    } else {
      startListening();
    }
  };

  const refreshHistory = React.useCallback(() => fetchHistory().then(setHistory).catch(() => {}), []);

  const handleAnalyze = React.useCallback(async (content, type) => {
    setLoading(true);
    setError("");
    setResult(null);
    
    try {
      let r;
      if (type === "text") {
        r = await analyzeText({ content_type: "text", model: selectedModel, text: content });
      } else if (type === "url") {
        r = await analyzeText({ content_type: "url", model: selectedModel, text: content });
      } else if (type === "pdf" && file) {
        r = await analyzeUpload(file, "pdf", selectedModel);
      } else if (type === "image" && file) {
        r = await analyzeUpload(file, "image", selectedModel);
      }
      
      setResult(r);
      refreshHistory();
      
      setTimeout(() => {
        resultsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 600);
    } catch (e) {
      const msg = e?.message || "Analysis failed. Please try again.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    } finally {
      setLoading(false);
    }
  }, [selectedModel, file, refreshHistory]);

  useEffect(() => {
    fetchModels().then((data) => {
      // Filter out fallback from UI - it's internal mechanism only
      const uiModels = data.filter(m => m.id !== "fallback");
      setModels(uiModels);
      // Select first available non-fallback model by default
      const availableModel = uiModels.find(m => m.available);
      if (availableModel) {
        setSelectedModel(availableModel.id);
      }
    }).catch(() => {
      const availableModels = [
        { id: "llama-3.3-70b-versatile", label: "Netra Hyper Engine", provider: "groq", available: true },
        { id: "gemini-2.0-flash", label: "Netra Hyper Intelligence", provider: "google", available: true },
        { id: "qwen3:8b", label: "Netra Core", provider: "ollama", available: true }
      ];
      setModels(availableModels);
      setSelectedModel(availableModels[0].id);
    });
    refreshHistory();
  }, [refreshHistory]);

  // Handle pending verification from voice mode
  useEffect(() => {
    const pendingVerification = sessionStorage.getItem('pendingVerification');
    if (pendingVerification) {
      try {
        const data = JSON.parse(pendingVerification);
        if (Date.now() - data.timestamp < 300000 && data.content) {
          setPrefilledText(data.content);
          setText(data.content);
          if (data.autoSubmit && data.content.length >= 30) {
            setTimeout(() => {
              handleAnalyze(data.content, "text");
            }, 800);
          }
        }
        sessionStorage.removeItem('pendingVerification');
      } catch (e) {
        sessionStorage.removeItem('pendingVerification');
      }
    }
  }, [handleAnalyze]);

  // Prefilled text effect
  useEffect(() => {
    if (prefilledText) {
      setText(prefilledText);
    }
  }, [prefilledText]);

  useEffect(() => {
    if (!loading) { setStageIdx(0); return; }
    const t = setInterval(() => setStageIdx((i) => (i + 1) % LOADING_STAGES.length), 2400);
    return () => clearInterval(t);
  }, [loading]);



  const handleSubmit = () => {
    if (activeTab === "text" && text.trim().length >= 30) {
      handleAnalyze(text.trim(), "text");
    } else if (activeTab === "url" && /^https?:\/\//.test(url.trim())) {
      handleAnalyze(url.trim(), "url");
    } else if ((activeTab === "pdf" || activeTab === "image") && file) {
      handleAnalyze(null, activeTab);
    }
  };

  const handleSelectHistory = async (id) => {
    setLoading(true);
    setError("");
    try {
      const r = await fetchAnalysis(id);
      setResult(r);
      setTimeout(() => {
        resultsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    } catch {
      setError("Couldn't load that analysis.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHistory = async (id) => {
    await deleteAnalysis(id).catch(() => {});
    if (result?.id === id) setResult(null);
    refreshHistory();
  };

  const handleReset = () => {
    setResult(null);
    setText("");
    setUrl("");
    setFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const loadExample = (exampleText) => {
    setText(exampleText);
    setShowExamples(false);
  };

  const canSubmit = 
    !loading && 
    ((activeTab === "text" && text.trim().length >= 30) ||
     (activeTab === "url" && /^https?:\/\//.test(url.trim())) ||
     ((activeTab === "pdf" || activeTab === "image") && file));

  const currentModel = models.find((m) => m.id === selectedModel);
  const selectedModelLabel = currentModel?.label || "AI Model";
  const isModelAvailable = currentModel?.available !== false;

  return (
    <div className="min-h-screen bg-ink-base text-white flex flex-col">
      <Header variant="app" onVoiceClick={handleVoiceTrigger} />

      {/* Hero Section - Matching Home Page Style */}
      <section className="border-b border-ink-border-subtle bg-gradient-to-br from-ink-base via-ink-base to-ink-surface/20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-8 md:py-12">
          {/* Header text for workspace */}
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-4">
              Intelligence <span className="text-brand">Workspace</span>
            </h1>
            <p className="text-zinc-400 text-lg">
              Deploy N.E.T.R.A's analytical lenses on any content to reveal hidden bias, 
              verify claims, and ensure source integrity.
            </p>
          </div>
        </div>
      </section>

      {/* Analysis Animation */}
      {loading && <AnalysisAnimation stage={stageIdx} />}

      {/* Results Section */}
      {result && !loading && (
        <div ref={resultsSectionRef} className="max-w-[1400px] mx-auto px-6 md:px-8 py-8 w-full">
          <ResultsDashboard result={result} onReset={handleReset} />
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1">
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Main Analyzer Panel */}
            <div ref={inputSectionRef} className="lg:col-span-8">
              {/* Input Card */}
              <div className="rounded-2xl border border-ink-border-subtle bg-ink-surface/70 backdrop-blur shadow-2xl shadow-black/40">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-ink-border-subtle">
                  <div className="flex items-center gap-2">
                    <Sparkles size={16} className="text-brand" />
                    <span className="text-white font-medium">Content Analysis</span>
                  </div>
                  
                  {/* Status indicator only, no model name exposed */}
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <div className="w-1.5 h-1.5 rounded-full bg-signal-success animate-pulse" />
                    <span className="text-zinc-400 font-medium">N.E.T.R.A Intelligence</span>
                  </div>
                </div>

                {/* Input Tabs */}
                <div className="flex items-center border-b border-ink-border-subtle px-2 pt-2 gap-1 overflow-x-auto">
                  {INPUT_TABS.map((t) => {
                    const Icon = t.icon;
                    const isActive = activeTab === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => {
                          setActiveTab(t.id);
                          setFile(null);
                          setError("");
                        }}
                        className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-all ${
                          isActive
                            ? "text-brand bg-ink-elevated/50 border-b-2 border-brand"
                            : "text-zinc-400 hover:text-zinc-200 hover:bg-ink-elevated/30"
                        }`}
                      >
                        <Icon size={16} />
                        {t.label}
                      </button>
                    );
                  })}
                </div>

                {/* Input Content */}
                <div className="p-6">
                  {/* Text Input */}
                  {activeTab === "text" && (
                    <div className="space-y-4">
                      <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder={INPUT_TABS[0].hint}
                        className="w-full h-48 bg-ink-base border border-ink-border-subtle rounded-xl p-4 text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-brand/50 focus:ring-1 focus:ring-brand/20 resize-none"
                      />
                      
                      {/* Examples */}
                      <div className="relative" style={{ zIndex: showExamples ? 50 : 'auto' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowExamples(!showExamples);
                            setShowModelSelect(false);
                          }}
                          className="text-sm text-brand hover:text-brand-soft transition-colors flex items-center gap-1"
                        >
                          <Sparkles size={14} />
                          Try an example
                          <ChevronDown size={14} className={`transition-transform ${showExamples ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {showExamples && (
                          <>
                            {/* Backdrop */}
                            <div 
                              className="fixed inset-0 z-40" 
                              onClick={() => setShowExamples(false)}
                            />
                            {/* Dropdown */}
                            <div className="absolute top-full left-0 mt-2 w-80 sm:w-96 rounded-xl border border-ink-border-subtle bg-ink-surface shadow-2xl z-50 max-h-80 overflow-y-auto">
                              <div className="p-2">
                                <div className="text-xs text-zinc-500 px-3 py-2 uppercase tracking-wider font-medium">Select an example</div>
                                {EXAMPLES.map((ex, i) => (
                                  <button
                                    key={i}
                                    onClick={() => loadExample(ex.text)}
                                    className="w-full text-left p-3 rounded-lg hover:bg-ink-elevated transition-colors group"
                                  >
                                    <div className="text-sm font-medium text-zinc-300 mb-1 group-hover:text-white">{ex.label}</div>
                                    <div className="text-xs text-zinc-500 line-clamp-2">{ex.text}</div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      
                      <div className="flex justify-between items-center text-xs text-zinc-500">
                        <span>{text.length} characters</span>
                        <span>Min 30 characters</span>
                      </div>
                    </div>
                  )}

                  {/* URL Input */}
                  {activeTab === "url" && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Globe size={18} className="text-zinc-500" />
                        <input
                          type="url"
                          value={url}
                          onChange={(e) => setUrl(e.target.value)}
                          placeholder="https://example.com/news-article"
                          className="flex-1 bg-ink-base border border-ink-border-subtle rounded-xl px-4 py-3 text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-brand/50"
                        />
                      </div>
                      <p className="text-xs text-zinc-500">
                        We'll fetch and analyze the content from this URL.
                      </p>
                    </div>
                  )}

                  {/* File Upload */}
                  {(activeTab === "pdf" || activeTab === "image") && (
                    <div 
                      className="border-2 border-dashed border-ink-border-subtle rounded-xl p-8 text-center hover:border-brand/30 transition-colors cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const f = e.dataTransfer.files?.[0];
                        if (f) setFile(f);
                      }}
                      onDragOver={(e) => e.preventDefault()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={activeTab === "pdf" ? ".pdf" : "image/*"}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) setFile(f);
                        }}
                        className="hidden"
                      />
                      
                      {file ? (
                        <div className="flex items-center justify-center gap-3">
                          <FileUp size={24} className="text-brand" />
                          <div className="text-left">
                            <div className="text-zinc-200 font-medium">{file.name}</div>
                            <div className="text-xs text-zinc-500">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFile(null);
                            }}
                            className="p-1 hover:bg-ink-elevated rounded"
                          >
                            <X size={16} className="text-zinc-400" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <FileUp size={32} className="mx-auto text-zinc-500 mb-3" />
                          <p className="text-zinc-400 mb-1">Drop {activeTab === "pdf" ? "a PDF" : "an image"} here, or click to browse</p>
                          <p className="text-xs text-zinc-600">Max 25MB</p>
                        </>
                      )}
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="mt-6">
                    <button
                      onClick={handleSubmit}
                      disabled={!canSubmit}
                      className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-medium transition-all ${
                        canSubmit
                          ? "bg-brand text-ink-base hover:bg-brand-soft shadow-lg shadow-brand/20"
                          : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                      }`}
                    >
                      {loading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Sparkles size={18} />
                          Run Analysis
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="mt-6 bg-red-950/30 border border-red-900/50 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-red-200 text-sm font-medium mb-2">Analysis Failed</p>
                      <p className="text-red-300/80 text-sm">{error}</p>
                    </div>
                    <button
                      onClick={() => setError("")}
                      className="text-red-400 hover:text-red-300 transition-colors"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* Status bar */}
              <div className="mt-6 flex items-center gap-4 text-xs text-zinc-500">
                <div className="flex items-center gap-1.5">
                  {isModelAvailable ? (
                    <>
                      <div className="w-1.5 h-1.5 rounded-full bg-signal-success animate-pulse" />
                      <span className="text-zinc-400">System Functional</span>
                    </>
                  ) : (
                    <>
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                      <span className="text-amber-400/80">Netra Switch</span>
                    </>
                  )}
                </div>
                <span className="text-zinc-700">•</span>
                <span>Private to your session</span>
              </div>
            </div>

            {/* History Sidebar */}
            <div className="lg:col-span-4 sticky top-6 self-start">
              <div className="rounded-2xl border border-ink-border-subtle bg-ink-surface/70 backdrop-blur p-5">
                <div className="flex items-center gap-2 mb-4">
                  <History size={16} className="text-brand" />
                  <h3 className="text-white font-medium">Recent Analyses</h3>
                </div>
                
                {history.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock size={24} className="mx-auto text-zinc-600 mb-2" />
                    <p className="text-sm text-zinc-500">No analyses yet</p>
                    <p className="text-xs text-zinc-600 mt-1">Your history will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[350px] overflow-y-auto pr-1">
                    {history.map((item) => (
                      <div
                        key={item.id}
                        className="group p-3 rounded-xl bg-ink-elevated/50 hover:bg-ink-elevated transition-colors cursor-pointer border border-transparent hover:border-ink-border-subtle"
                        onClick={() => handleSelectHistory(item.id)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-zinc-300 truncate" title={item.title || "Analysis"}>
                              {item.title || "Analysis"}
                            </div>
                            <div className="text-xs text-zinc-500 mt-1">
                              {formatRelative(item.created_at || item.timestamp)}
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteHistory(item.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-950/30 rounded transition-all"
                          >
                            <Trash2 size={14} className="text-red-400" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick Tip */}
              <div className="mt-6 p-4 rounded-xl bg-signal-info/5 border border-signal-info/20">
                <p className="text-xs text-signal-info leading-relaxed">
                  <strong className="text-signal-info">Voice Intel:</strong> Bypass the interface entirely. Tap the microphone on the home page and speak directly to N.E.T.R.A to execute real-time, hands-free fact-checks.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* ── Floating Voice Button + Panel (chatbot style) ── */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">

        {/* Voice Panel, slides up above the button */}
        {showVoice && (
          <div className="w-[min(400px,calc(100vw-3rem))] animate-in fade-in slide-in-from-bottom-4 duration-300">
            <div className="relative group">
              <button
                onClick={() => { stopAll(); setShowVoice(false); }}
                className="absolute -top-2 -right-2 z-[60] bg-zinc-800 text-white p-1.5 rounded-full border border-zinc-700 shadow-xl opacity-0 group-hover:opacity-100 transition-opacity hover:bg-zinc-700"
                title="Close Voice Mode"
              >
                <X size={14} />
              </button>
              <JarvisVoicePanel
                supported={supported}
                phase={phase}
                language={language}
                setLanguage={setLanguage}
                userCaption={userCaption}
                aiCaption={aiCaption}
                isInterim={isInterim}
                audioLevel={audioLevel}
                analyser={analyser}
                onMicClick={handleVoiceTrigger}
                errorMsg={voiceError}
                voices={voices}
                selectedVoiceName={selectedVoiceName}
                preferredVoiceName={preferredVoiceName}
                setPreferredVoiceName={setPreferredVoiceName}
              />
            </div>
          </div>
        )}

        {/* Floating Trigger Button */}
        <div className="relative">
          {/* Outer glow ring when active */}
          {(showVoice || phase === "listening") && (
            <>
              <span className="absolute inset-0 rounded-full border border-cyan-400/40 animate-ping" />
              <span className="absolute -inset-2 rounded-full border border-cyan-400/20 animate-ping delay-150" />
            </>
          )}

          <button
            onClick={() => {
              if (showVoice) {
                stopAll();
                setShowVoice(false);
              } else {
                handleVoiceTrigger();
              }
            }}
            title={showVoice ? "Close voice mode" : "Ask N.E.T.R.A anything by voice"}
            className={`
              relative w-14 h-14 rounded-full shadow-2xl
              flex items-center justify-center
              transition-all duration-300 hover:scale-110 active:scale-95
              border-2
              ${showVoice
                ? phase === "listening"
                  ? "bg-cyan-500/20 border-cyan-400 shadow-cyan-500/40"
                  : phase === "thinking"
                  ? "bg-amber-500/20 border-amber-400 shadow-amber-500/40"
                  : phase === "speaking"
                  ? "bg-emerald-500/20 border-emerald-400 shadow-emerald-500/40"
                  : "bg-brand/20 border-brand shadow-brand/30"
                : "bg-zinc-900 border-zinc-700 hover:border-brand/60 hover:bg-zinc-800 hover:shadow-brand/20"
              }
            `}
          >
            {/* Icon */}
            {showVoice && phase === "thinking" ? (
              <Loader2 size={22} className="text-amber-400 animate-spin" />
            ) : showVoice && phase === "speaking" ? (
              <Sparkles size={22} className="text-emerald-400 animate-pulse" />
            ) : showVoice ? (
              <Mic size={22} className={phase === "listening" ? "text-cyan-400" : "text-brand"} />
            ) : (
              <Mic size={22} className="text-zinc-300 group-hover:text-brand transition-colors" />
            )}
          </button>

          {/* Tooltip label */}
          {!showVoice && (
            <div className="absolute bottom-full right-0 mb-2 pointer-events-none">
              <div className="bg-zinc-900 border border-zinc-700 text-zinc-200 text-xs px-3 py-1.5 rounded-full whitespace-nowrap shadow-xl opacity-0 hover:opacity-100 transition-opacity">
                Ask N.E.T.R.A by voice
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
